// Branch Builder - Interface for adding branches to patterns
// Allows users to specify attachment points and build branch structures
// Supports two modes:
// - 'standalone': For branch-only search (no attachment position needed)
// - 'attached': For anywhere search (requires attachment position)

import { useState } from 'react';
import { Icon, Dropdown, Button } from 'semantic-ui-react';
import SNFGsvg from '../../visualizer-tool/glycan-interpreter/snfgSVG';
import BondSelector from './BondSelector';
import resdict from '../../visualizer-tool/data/resdict.json';
import '../../css/advanced-search.css';

export default function BranchBuilder({ pattern, setPattern, onClose, mode = 'attached' }) {
  // mode: 'standalone' (branch-only) or 'attached' (anywhere with main chain)
  const isStandaloneMode = mode === 'standalone';
  const [currentBranch, setCurrentBranch] = useState({
    attachToPosition: 0,
    sugars: [],
    bonds: [], // Internal bonds within the branch
    attachmentBond: null // How the branch connects to main chain
  });
  const [selectedSugar, setSelectedSugar] = useState('');

  // Get attachment position options from main chain
  const getAttachmentOptions = () => {
    return pattern.mainChain.map((sugar, index) => ({
      key: index,
      text: `Position ${index + 1}: ${sugar.sugar}`,
      value: index
    }));
  };

  // Get common sugars for dropdown
  const getSugarOptions = () => {
    const commonSugars = ['GlcA', 'Fuc', 'Rha', 'GlcNAc', 'ManNAc', 'GalNAc', 'Neu5Ac', 'Man', 'Gal', 'Glc'];
    return commonSugars
      .filter(sugar => resdict[sugar])
      .map(sugar => ({
        key: sugar,
        text: sugar,
        value: sugar
      }));
  };

  // Add sugar to current branch
  const addSugarToBranch = () => {
    if (!selectedSugar) return;

    const sugarData = resdict[selectedSugar];
    if (!sugarData) return;

    const newSugar = {
      id: `branch-${selectedSugar}-${Date.now()}`,
      sugar: selectedSugar,
      baseSugar: selectedSugar,
      shape: sugarData.shape,
      color: sugarData.colour,
      isWildcard: false
    };

    setCurrentBranch(prev => ({
      ...prev,
      sugars: [...prev.sugars, newSugar],
      bonds: [...prev.bonds, null] // Add placeholder for bond after this sugar
    }));

    setSelectedSugar('');
  };

  // Update bond within the branch
  const updateBranchBond = (sugarIndex, bond) => {
    setCurrentBranch(prev => {
      const newBonds = [...prev.bonds];
      newBonds[sugarIndex] = bond;
      return {
        ...prev,
        bonds: newBonds
      };
    });
  };

  // Update attachment bond (how branch connects to main chain)
  const updateAttachmentBond = (bond) => {
    setCurrentBranch(prev => ({
      ...prev,
      attachmentBond: bond
    }));
  };

  // Remove sugar from current branch
  const removeSugarFromBranch = (sugarId) => {
    setCurrentBranch(prev => {
      const sugarIndex = prev.sugars.findIndex(s => s.id === sugarId);
      if (sugarIndex === -1) return prev;

      const newSugars = prev.sugars.filter(s => s.id !== sugarId);
      const newBonds = [...prev.bonds];
      newBonds.splice(sugarIndex, 1); // Remove corresponding bond

      return {
        ...prev,
        sugars: newSugars,
        bonds: newBonds
      };
    });
  };

  // Save branch to pattern
  const saveBranch = () => {
    if (currentBranch.sugars.length === 0) {
      alert('Please add at least one sugar to the branch');
      return;
    }

    const newBranch = {
      id: `branch-${Date.now()}`,
      attachToPosition: currentBranch.attachToPosition,
      sugars: currentBranch.sugars,
      bonds: currentBranch.bonds,
      attachmentBond: currentBranch.attachmentBond
    };

    setPattern(prev => ({
      ...prev,
      branches: [...(prev.branches || []), newBranch]
    }));

    // Reset and close
    setCurrentBranch({
      attachToPosition: 0,
      sugars: [],
      bonds: [],
      attachmentBond: null
    });
    onClose();
  };

  return (
    <div className="branch-builder">
      <div className="branch-builder-header">
        <h4>
          <Icon name="fork" />
          {isStandaloneMode ? 'Build Branch Pattern' : 'Build Branch'}
        </h4>
        <button onClick={onClose} className="close-branch-builder">
          <Icon name="close" />
        </button>
      </div>

      <div className="branch-builder-content">
        {/* Info message for standalone mode */}
        {isStandaloneMode && (
          <div style={{
            background: '#f9f9f9',
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '15px',
            border: '1px solid #ddd',
            fontSize: '0.9em',
            color: '#555'
          }}>
            <Icon name="info circle" />
            Build a sugar pattern to search within branches. No attachment position needed—this pattern will be searched across all branches in the structure.
          </div>
        )}

        {/* Attachment Point Selection - Only for 'attached' mode */}
        {!isStandaloneMode && (
          <div className="branch-setting">
            <label>
              <Icon name="chain" />
              Attach branch after:
            </label>
            <Dropdown
              placeholder="Select attachment position"
              selection
              options={getAttachmentOptions()}
              value={currentBranch.attachToPosition}
              onChange={(e, { value }) =>
                setCurrentBranch(prev => ({ ...prev, attachToPosition: value }))
              }
            />
          </div>
        )}

        {/* Branch Preview */}
        <div className="branch-preview">
          <strong>Branch Structure:</strong>
          {currentBranch.sugars.length === 0 ? (
            <div className="empty-branch-message">
              No sugars added yet. Select a sugar below to add.
            </div>
          ) : (
            <div className="branch-chain">
              {currentBranch.sugars.map((sugar, index) => (
                <div key={sugar.id} className="sugar-in-branch">
                  <div className="sugar-item small">
                    <div className="sugar-shape small">
                      <SNFGsvg snfg={sugar.sugar} />
                    </div>
                    <div className="sugar-name small">{sugar.sugar}</div>
                    <button
                      className="remove-sugar-btn small"
                      onClick={() => removeSugarFromBranch(sugar.id)}
                    >
                      <Icon name="close" size="small" />
                    </button>
                  </div>
                  {index < currentBranch.sugars.length - 1 && (
                    <div className="branch-bond-selector">
                      <BondSelector
                        sugarIndex={index}
                        bond={currentBranch.bonds[index]}
                        onBondChange={updateBranchBond}
                        fromSugar={sugar}
                        toSugar={currentBranch.sugars[index + 1]}
                        compact={true}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Attachment Linkage Selector - Only for 'attached' mode */}
        {!isStandaloneMode && currentBranch.sugars.length > 0 && (
          <div className="attachment-bond-section">
            <label>
              <Icon name="linkify" />
              Attachment Linkage (branch → main chain):
            </label>
            <div className="attachment-bond-info">
              <p className="help-text">
                <strong>Optional:</strong> Specify how the first sugar in the branch ({currentBranch.sugars[0]?.sugar})
                connects to <strong>{pattern.mainChain[currentBranch.attachToPosition]?.sugar}</strong>
                in the main chain. Leave blank to search for any attachment linkage.
              </p>
              <BondSelector
                sugarIndex={-1}
                bond={currentBranch.attachmentBond}
                onBondChange={(idx, bond) => updateAttachmentBond(bond)}
                fromSugar={currentBranch.sugars[0]}
                toSugar={pattern.mainChain[currentBranch.attachToPosition]}
                compact={false}
                label="Attachment:"
              />
            </div>
          </div>
        )}

        {/* Add Sugar to Branch */}
        <div className="add-sugar-section">
          <label>
            <Icon name="plus" />
            Add sugar to branch:
          </label>
          <div className="add-sugar-controls">
            <Dropdown
              placeholder="Select sugar"
              selection
              search
              options={getSugarOptions()}
              value={selectedSugar}
              onChange={(e, { value }) => setSelectedSugar(value)}
            />
            <Button
              onClick={addSugarToBranch}
              disabled={!selectedSugar}
              primary
            >
              <Icon name="plus" />
              Add
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="branch-builder-actions">
          <Button onClick={onClose} basic>
            Cancel
          </Button>
          <Button
            onClick={saveBranch}
            disabled={currentBranch.sugars.length === 0}
            primary
          >
            <Icon name="check" />
            Save Branch
          </Button>
        </div>
      </div>
    </div>
  );
}
